import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Plus, Calendar, Edit2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Allergies = ({ baby }) => {
  const navigate = useNavigate();
  // Always read-only for baby/parent users - only doctors can edit
  const viewOnlyMode = true;
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAllergy, setEditingAllergy] = useState(null);
  const [formData, setFormData] = useState({
    allergen: '',
    reaction: '',
    severity: '',
    discovered_date: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Handle date input with auto-advance
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, discovered_date: value }));
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, discovered_date: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  useEffect(() => {
    fetchAllergies();
  }, [baby]);

  const fetchAllergies = async () => {
    try {
      const response = await axios.get(`${API}/allergies/${baby.id}`);
      setAllergies(response.data);
    } catch (error) {
      console.error('Error fetching allergies:', error);
      toast.error('Failed to load allergies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllergy = async (e) => {
    e.preventDefault();
    
    if (!formData.allergen || !formData.reaction || !formData.severity || !formData.discovered_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const allergyData = {
        baby_id: baby.id,
        allergen: formData.allergen,
        reaction: formData.reaction,
        severity: formData.severity,
        discovered_date: formData.discovered_date,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/allergies`, allergyData);
      toast.success('Allergy added successfully!');
      setShowAddDialog(false);
      setFormData({
        allergen: '',
        reaction: '',
        severity: '',
        discovered_date: '',
        notes: ''
      });
      fetchAllergies();
    } catch (error) {
      console.error('Error adding allergy:', error);
      toast.error('Failed to add allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAllergy = (allergy) => {
    setEditingAllergy(allergy);
    setFormData({
      allergen: allergy.allergen,
      reaction: allergy.reaction,
      severity: allergy.severity,
      discovered_date: allergy.discovered_date,
      notes: allergy.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateAllergy = async (e) => {
    e.preventDefault();
    
    if (!formData.allergen || !formData.reaction || !formData.severity || !formData.discovered_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/allergies/${editingAllergy.id}`, formData);
      toast.success('Allergy updated successfully!');
      setShowEditDialog(false);
      setEditingAllergy(null);
      setFormData({
        allergen: '',
        reaction: '',
        severity: '',
        discovered_date: '',
        notes: ''
      });
      fetchAllergies();
    } catch (error) {
      console.error('Error updating allergy:', error);
      toast.error('Failed to update allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAllergy = async (allergyId) => {
    if (!window.confirm('Are you sure you want to delete this allergy?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/allergies/${allergyId}`);
      toast.success('Allergy deleted successfully');
      fetchAllergies();
    } catch (error) {
      console.error('Error deleting allergy:', error);
      toast.error('Failed to delete allergy');
    }
  };

  const formatDate = (dateString) => {
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getSeverityBadge = (severity) => {
    const severityClasses = {
      mild: 'bg-yellow-100 text-yellow-800',
      moderate: 'bg-orange-100 text-orange-800',
      severe: 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge variant="outline" className={severityClasses[severity] || 'bg-gray-100 text-gray-800'}>
        {severity.charAt(0).toUpperCase() + severity.slice(1)}
      </Badge>
    );
  };

  const getSeverityIcon = (severity) => {
    const iconColors = {
      mild: 'text-yellow-600',
      moderate: 'text-orange-600',
      severe: 'text-red-600'
    };
    
    return <AlertCircle className={`w-5 h-5 ${iconColors[severity] || 'text-gray-600'}`} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading allergies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Allergies</h1>
                <p className="text-blue-200 text-sm">{baby.name}</p>
              </div>
            </div>
          </div>
          
          {!viewOnlyMode && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white"
                data-testid="add-allergy-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Allergy
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Allergy</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddAllergy} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="allergen">Allergen *</Label>
                  <Input
                    id="allergen"
                    placeholder="e.g., Peanuts, Milk, Eggs"
                    value={formData.allergen}
                    onChange={(e) => setFormData(prev => ({ ...prev, allergen: e.target.value }))}
                    data-testid="allergen-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reaction">Reaction *</Label>
                  <Input
                    id="reaction"
                    placeholder="e.g., Rash, Difficulty breathing"
                    value={formData.reaction}
                    onChange={(e) => setFormData(prev => ({ ...prev, reaction: e.target.value }))}
                    data-testid="reaction-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Severity *</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))} required>
                    <SelectTrigger data-testid="severity-select">
                      <SelectValue placeholder="Select severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mild">Mild</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="discovered_date">Discovered Date *</Label>
                  <Input
                    id="discovered_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={formData.discovered_date}
                    onChange={handleDateInput}
                    onKeyDown={handleDateKeyDown}
                    maxLength={10}
                    data-testid="discovered-date-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Additional notes about the allergy..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    data-testid="allergy-notes-input"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={submitting}
                    data-testid="save-allergy-button"
                  >
                    {submitting ? 'Saving...' : 'Save Allergy'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}

          {/* Edit Allergy Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="sm:max-w-md z-50">
              <DialogHeader>
                <DialogTitle>Edit Allergy</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateAllergy} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_allergen">Allergen Name *</Label>
                  <Input
                    id="edit_allergen"
                    placeholder="e.g., Peanuts, Eggs"
                    value={formData.allergen}
                    onChange={(e) => setFormData(prev => ({ ...prev, allergen: e.target.value }))}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_reaction">Reaction *</Label>
                  <Input
                    id="edit_reaction"
                    placeholder="e.g., Hives, Swelling"
                    value={formData.reaction}
                    onChange={(e) => setFormData(prev => ({ ...prev, reaction: e.target.value }))}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Severity *</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))} value={formData.severity} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mild">Mild</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_discovered_date">Discovered Date *</Label>
                  <Input
                    id="edit_discovered_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={formData.discovered_date}
                    onChange={handleDateInput}
                    onKeyDown={handleDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes</Label>
                  <Textarea
                    id="edit_notes"
                    placeholder="Additional information"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={submitting}
                  >
                    {submitting ? 'Updating...' : 'Update Allergy'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingAllergy(null);
                      setFormData({
                        allergen: '',
                        reaction: '',
                        severity: '',
                        discovered_date: '',
                        notes: ''
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="px-6 py-6">
        {allergies.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="no-allergies">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Allergies</h3>
            <p className="text-gray-600 mb-4">No allergies recorded for {baby.name}.</p>
            {!viewOnlyMode && (
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-first-allergy-button">
                <Plus className="w-4 h-4 mr-2" />
                Add First Allergy
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {allergies.map((allergy) => (
              <div key={allergy.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid={`allergy-${allergy.id}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getSeverityIcon(allergy.severity)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                          {allergy.allergen}
                        </h3>
                        {getSeverityBadge(allergy.severity)}
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-500 flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(allergy.discovered_date)}</span>
                        </div>
                        {!viewOnlyMode && (
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditAllergy(allergy)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteAllergy(allergy.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600">
                      <div><strong>Reaction:</strong> {allergy.reaction}</div>
                      {allergy.notes && (
                        <div className="pt-2 border-t border-gray-100">
                          <strong>Notes:</strong> {allergy.notes}
                        </div>
                      )}
                    </div>
                    {allergy.updated_by_doctor && (
                      <p className="text-xs text-purple-600 mt-2">
                        Updated by {allergy.updated_by_doctor}
                        {allergy.updated_by_hospital && ` - ${allergy.updated_by_hospital}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Allergies;